/**
 * The per-selection editor section: dispatches on the selection kind to the
 * matching inspector (edge / alcove-wall titles, corner chamfer, edge item,
 * line segment, free element). Contextual how-to hints live separately
 * (selectionHint.ts, pinned in the panel's footer).
 */

import { FloorPlanEditorState, getLineSegment, useFloorPlanEditorState } from '@kaiizen/kai-floor-plan-editor-core';
import { AlcoveInspector, LineSegInspector } from './AlcoveInspector';
import { SECTION_STYLE } from './chrome';
import { CommitLengthField } from './fields';
import { FreeElementInspector } from './FreeElementInspector';
import { RoomFields } from './RoomSection';

function inspectorBody(floorPlanEditorState: FloorPlanEditorState): React.JSX.Element | null {
  const state = floorPlanEditorState.st, shape = state.shape, selection = state.selection;
  if (selection.kind === 'room') {
    // The same numbers the nothing-selected view shows — one presentation.
    return (
      <>
        <h2>Room</h2>
        <RoomFields />
      </>
    );
  }
  if (selection.kind === 'edge') {
    return <h2>Edge · {selection.edge}</h2>;
  }
  if (selection.kind === 'alcoveWall') {
    return <h2>Alcove {selection.wall} wall</h2>;
  }
  if (selection.kind === 'corner') {
    const corner = shape[selection.corner] ?? { dx: 0, dy: 0 };
    return (
      <>
        <h2>Corner · {selection.corner}</h2>
        <CommitLengthField label='Horizontal (dx)' setKey='corner-dx' value={corner.dx} />
        <CommitLengthField label='Vertical (dy)' setKey='corner-dy' value={corner.dy} />
      </>
    );
  }
  if (selection.kind === 'alcove' || selection.kind === 'alcoveCorner') {
    const edgeSegment = shape[selection.edge]?.segments[selection.index];
    if (edgeSegment) return <AlcoveInspector edgeSegment={edgeSegment} edge={selection.edge} index={selection.index} />;
  } else if (selection.kind === 'lineSeg') {
    const edgeSegment = getLineSegment(state);
    if (edgeSegment) return <LineSegInspector edgeSegment={edgeSegment} lineEdgeRef={selection.ref} />;
  } else if (selection.kind === 'inner' || selection.kind === 'innerCorner') {
    const freeElement = shape.freeElements?.[selection.index];
    if (freeElement) return <FreeElementInspector freeElement={freeElement} />;
  }
  return null;
}

export function SelectionInspector(): React.JSX.Element | null {
  const floorPlanEditorState = useFloorPlanEditorState();
  const body = inspectorBody(floorPlanEditorState);
  if (!body) return null;
  // Re-key the section per selection identity so uncontrolled inputs never
  // carry typed-but-uncommitted text between two entities with equal values.
  return (
    <div css={SECTION_STYLE} key={JSON.stringify(floorPlanEditorState.st.selection)}>
      {body}
    </div>
  );
}
